import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { isCarouselPath, PANEL_SLUGS } from "./components/Portfolio/CaseStudy/registry";
import { prefetchCarouselStripPages } from "./components/Portfolio/stripPageUrls";
import { ROUTES } from "./routes";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import PageTransition from "./components/PageTransition/PageTransition";
import { SectionProvider } from "./components/Index/SectionContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { animationConfig } from "./config/animation";

// Push animation tokens that static CSS in src/styles/index.css needs to
// read (skills-tab transitions, About icon keyframes). Single module-level
// side effect — runs once at import time, before any component renders, so
// the first paint already has the right values. Adding a new CSS-driven
// animation? Mirror its duration here and reference `var(--...)` in the
// rule, instead of hardcoding.
if (typeof document !== "undefined") {
    const root = document.documentElement.style;
    const T = animationConfig.landing.skillsTabs;
    root.setProperty("--tabs-transition-duration", `${T.durationS}s`);
    root.setProperty("--tabs-transition-easing", T.easing);
    const A = animationConfig.about;
    root.setProperty("--sound-wave-duration", `${A.soundWave.durationS}s`);
    root.setProperty("--mute-shake-duration", `${A.muteShake.durationS}s`);
    root.setProperty("--shackle-float-duration", `${A.shackleFloat.durationS}s`);
    root.setProperty("--lock-press-duration", `${A.lockPress.durationS}s`);
}

// Lazy-load all page components so each route only evaluates the JS it
// needs (see src/routes.ts for the map). Critically, this keeps Framer
// Motion (~100 KB) off the carousel and the book, where it would be a
// ~2 s long task.
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Book = lazy(() => import("./pages/Book"));
const NotFound = lazy(() => import("./pages/NotFound"));

function App() {
    // Prefetch off-path route chunks once the page has loaded and the
    // browser is idle, so their JS is parsed before the user navigates.
    // Hover preloads (in Nav) handle the warm path; this covers users who
    // touch-tap or click without hovering. Waiting for the load event
    // (not just idle, which a fast connection reaches before the first
    // paint) keeps these off the page's own critical path.
    //
    // The carousel's strip pages ride along, but only where the carousel
    // is not mounted (the book, the 404). On / and /<slug> Portfolio is
    // already streaming them, and index.html preloads them on /: a
    // prefetch there would be a second download of each page. The route
    // table below is what decides where Portfolio mounts, so the check
    // lives here, next to it, rather than in index.html's head script,
    // which cannot read the slug list.
    useEffect(() => {
        const schedule = (fn: () => void) => {
            if ("requestIdleCallback" in window) {
                (window as Window & typeof globalThis).requestIdleCallback(fn, { timeout: 3000 });
            } else {
                setTimeout(fn, 2000);
            }
        };
        const prefetch = () => {
            schedule(() => {
                void import("./components/Portfolio/AboutPanel");
                void import("./pages/Portfolio");
                if (!isCarouselPath(window.location.pathname)) prefetchCarouselStripPages();
            });
            // The book is unlinked but one tap from the About panel.
            schedule(() => {
                void import("./pages/Book");
                void import("./components/About/BookCanvas");
            });
        };

        if (document.readyState === "complete") {
            prefetch();
            return;
        }
        window.addEventListener("load", prefetch, { once: true });
        return () => window.removeEventListener("load", prefetch);
    }, []);

    return (
        <Router>
            <PageTransition>
                <SectionProvider>
                    <Nav />
                    {/* ErrorBoundary wraps Routes (not Nav/Footer) so a route
                        crash still leaves navigation working — user can click
                        to a different route to recover without a full reload. */}
                    <ErrorBoundary>
                        <Suspense fallback={null}>
                            <Routes>
                                {/* One child route per panel keeps Portfolio
                                    (heavy WebGL) mounted across / ↔ /<slug>, so
                                    the panel slides over a live carousel
                                    instead of remounting it. element={null}:
                                    Portfolio reads the slug via useMatch and
                                    renders the overlay itself. Each slug is
                                    declared (rather than ":slug") so every
                                    other path falls through to the 404. */}
                                <Route path={ROUTES.index} element={<Portfolio />}>
                                    {PANEL_SLUGS.map((slug) => (
                                        <Route key={slug} path={slug} element={null} />
                                    ))}
                                </Route>
                                {/* The old About page: unlinked, see src/routes.ts. */}
                                <Route path={ROUTES.book} element={<Book />} />
                                {/* The 404 is the old landing page, the avatar office. */}
                                <Route path="*" element={<NotFound />} />
                            </Routes>
                        </Suspense>
                    </ErrorBoundary>
                    <Footer />
                </SectionProvider>
            </PageTransition>
        </Router>
    );
}

export default App;
